import { FeatureFlag } from '@ps/web-core-enums';
import React from 'react';
import { useSelector } from 'react-redux';

import Redirect from '@swf/components/Redirect/Redirect';
import withWidgetTestId from '@swf/core/hoc/withWidgetTestId';
import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';
import type { WidgetFC } from '@swf/core/types/Widget';
import { BetsLink } from '@swf/sports/components';
import LiveDataWarning from '@swf/sports/components/LiveDataWarning/LiveDataWarning';
import LoginToViewBets from '@swf/sports/components/LoginToViewBets/LoginToViewBets';
import { getMyBetsAlias } from '@swf/sports/utils/getMyBetsAlias';

import Header from './components/Header/Header';
import MyBets from './components/MyBets/MyBets';
import MyBetsWithFilters from './components/MyBetsWithFilters/MyBetsWithFilters';
import Tabs from './components/Tabs/Tabs';
import { sportsMyBetsWidgetModel, useSportsMyBetsWidget } from './model';

const SportsMyBetsWidget: WidgetFC = () => {
  const {
    widgetConfig: { isEmbedded },
    widgetSelect
  } = useSportsMyBetsWidget();
  const { status } = useSelector(select.sports.getUrlParams);
  const isSettled = status === 'settled';
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const isSelfExcluded = useSelector(select.account.getSelfExclusion);
  const launchDate = useSelector(select.cms.getWebsiteLaunchDate);
  const sportsHistoricalBetsFlag = useFeature(FeatureFlag.ISPHistoricalBets);
  const cashedOut = useSelector(widgetSelect.cashedOut);

  if (status !== 'open' && status !== 'settled') {
    return (
      <Redirect alias={getMyBetsAlias(isSelfExcluded, isEmbedded)} values={{ status: 'open' }} />
    );
  }

  return (
    <>
      <Header />
      {!isAuthenticated ? (
        <LoginToViewBets />
      ) : (
        <>
          <Tabs isSelfExcluded={isSelfExcluded} isEmbedded={isEmbedded} />
          {isSettled ? (
            <MyBetsWithFilters cashedOut={cashedOut} />
          ) : (
            <MyBets cashedOut={cashedOut} />
          )}

          {sportsHistoricalBetsFlag && launchDate && (
            <BetsLink
              type="historical-bets"
              status={status}
              isSelfExcluded={isSelfExcluded}
              isEmbedded={isEmbedded}
            />
          )}

          {isSettled ? null : <LiveDataWarning />}
        </>
      )}
    </>
  );
};

SportsMyBetsWidget.model = sportsMyBetsWidgetModel;

export default withWidgetTestId(SportsMyBetsWidget, 'sports-my-bets');
